import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../App.css";
import clearBadgeMark from "../assets/clearbadge-icon-color.png";
import {
  EmailIcon,
  PhoneIcon,
  SaveIcon,
  LicenseIcon,
  CompanyIcon,
  TwitterIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  BusinessAddressIcon,
  UserIcon,
  GlobeIcon,
} from "../assets/icons/icons";
import { supabase } from "../supabaseClient";
import QRCode from "qrcode";
import { DEFAULT_CARD_COLOR } from "../constants/cardDefaults";
import { trackGoal } from "../utils/datafast";
import { sortLicensesByState } from "../utils/licenses";
import useFocusTrap from "../hooks/useFocusTrap";

const API_BASE = process.env.REACT_APP_API_URL || "";

const getDeviceType = () => {
  const ua = navigator.userAgent || "";
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ? "mobile" : "desktop";
};

export default function DigitalCard({
  profile,
  initials,
  licenses = [],
  stats = {},
  cardColor = DEFAULT_CARD_COLOR,
  cardBackgroundUrl = "",
  publicSlug = "",
  profileId = "",
}) {
  const accent = cardColor || DEFAULT_CARD_COLOR;
  const sortedLicenses = useMemo(() => sortLicensesByState(licenses), [licenses]);
  const hasLicense = sortedLicenses.length > 0;
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [submittingLead, setSubmittingLead] = useState(false);
  const modalTimerRef = useRef(null);
  const leadModalRef = useRef(null);
  const leadSkipButtonRef = useRef(null);
  const thankYouModalRef = useRef(null);
  const thankYouCloseButtonRef = useRef(null);
  const licenseModalRef = useRef(null);
  const licenseModalCloseRef = useRef(null);
  const [thankYouOpen, setThankYouOpen] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [badgeStatus, setBadgeStatus] = useState({
    state: "loading",
    verified: null,
    checkedAt: null,
    lastVerifiedAt: null,
    profileUrl: "",
    lookupUrl: "",
  });

  const licenseLabel = (lic) =>
    `${lic.industry || "License"}${lic.state ? ` - ${lic.state}` : ""}`;

  const handleOpenLicense = async (license) => {
    if (!license) {
      return;
    }
    const logLicenseView = async () => {
      if (!publicSlug) return;
      const licenseId = license.id || license.licenseId || license.license_id || null;
      const isUuid =
        typeof licenseId === "string" &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(licenseId);
      const parts = [license.industry, license.state].filter(Boolean).join(" - ");
      const number = license.licenseNumber || license.license_number || "";
      const numberLabel = number ? ` #${number}` : "";
      const label = parts ? `${parts}${numberLabel}` : "License";
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token || "";
        await fetch(`${API_BASE}/api/leads`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            publicSlug,
            leadType: "license_view",
            viewTargetType: "license",
            viewTargetLabel: label,
            ...(isUuid ? { viewTargetId: licenseId } : {}),
            deviceType: getDeviceType(),
          }),
        });
      } catch (_err) {
        // no-op
      }
    };
    await logLicenseView();
    if (license.docUrl) {
      window.open(license.docUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (license.supportingDocument) {
      try {
        const { data: signedDoc } = await supabase.storage
          .from("license_docs")
          .createSignedUrl(license.supportingDocument, 3600);
        if (signedDoc?.signedUrl) {
          window.open(signedDoc.signedUrl, "_blank", "noopener,noreferrer");
          return;
        }
      } catch (err) {
        // swallow silently
      }
    }
  };

  const clamp = (v, min = 0, max = 255) => Math.min(max, Math.max(min, v));
  const hexToRgb = (hex) => {
    const c = hex.replace("#", "");
    const full = c.length === 3 ? c.split("").map((ch) => ch + ch).join("") : c;
    const num = parseInt(full || "000000", 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255,
    };
  };

  const rgbToHex = (r, g, b) => `#${[r, g, b].map((v) => clamp(Math.round(v)).toString(16).padStart(2, "0")).join("")}`;

  const lighten = (hex, amount = 0.2) => {
    const { r, g, b } = hexToRgb(hex);
    return rgbToHex(r + (255 - r) * amount, g + (255 - g) * amount, b + (255 - b) * amount);
  };

  const darken = (hex, amount = 0.15) => {
    const { r, g, b } = hexToRgb(hex);
    return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
  };

  const grayscale = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    const y = clamp(0.299 * r + 0.587 * g + 0.114 * b);
    return { hex: rgbToHex(y, y, y), rgb: `${Math.round(y)}, ${Math.round(y)}, ${Math.round(y)}` };
  };

  const accentLight = lighten(accent, 0.6);
  const accentLightBorder = lighten(accent, 0.5);
  const accentDark = darken(accent, 0.2);
  const gray = grayscale(accent);
  const overlayRgb = gray.rgb || "189, 189, 189";

  const normalizeSocialUrl = (value, baseUrl) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    const withoutAt = trimmed.replace(/^@/, "");
    if (withoutAt.includes(".") || withoutAt.includes("/")) {
      return `https://${withoutAt}`;
    }
    if (baseUrl) return `${baseUrl}${withoutAt}`;
    return `https://${withoutAt}`;
  };

  const normalizeWebsiteUrl = (value) => {
    if (!value) return "";
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith("//")) return `https:${trimmed}`;
    return `https://${trimmed}`;
  };

  const websiteLinks = useMemo(() => {
    const raw = Array.isArray(profile.links) ? profile.links : [];
    const cleaned = raw
      .map((l) => ({
        label: (l?.label || "").trim(),
        url: normalizeWebsiteUrl(l?.url || ""),
      }))
      .filter((l) => !!l.url);
    return cleaned;
  }, [profile.links]);

  const formatWebsiteLabel = (url, fallback = "Website") => {
    try {
      const u = new URL(url);
      const host = u.hostname.replace(/^www\./, "");
      return host || fallback;
    } catch {
      return fallback;
    }
  };

  const socialLinks = useMemo(() => {
    const links = [];
    const twitter = normalizeSocialUrl(profile.twitter, "https://twitter.com/");
    if (twitter && profile.visibility?.twitter !== false) links.push({ key: "twitter", url: twitter, label: "Twitter", Icon: TwitterIcon });
    const facebook = normalizeSocialUrl(profile.facebook, "https://facebook.com/");
    if (facebook && profile.visibility?.facebook !== false) links.push({ key: "facebook", url: facebook, label: "Facebook", Icon: FacebookIcon });
    const instagram = normalizeSocialUrl(profile.instagram, "https://instagram.com/");
    if (instagram && profile.visibility?.instagram !== false) links.push({ key: "instagram", url: instagram, label: "Instagram", Icon: InstagramIcon });
    const linkedin = normalizeSocialUrl(profile.linkedin, "https://www.linkedin.com/in/");
    if (linkedin && profile.visibility?.linkedin !== false) links.push({ key: "linkedin", url: linkedin, label: "LinkedIn", Icon: LinkedInIcon });
    return links;
  }, [profile.facebook, profile.instagram, profile.linkedin, profile.twitter, profile.visibility]);

  const businessAddressText = useMemo(() => {
    const parts = [
      profile.businessStreet,
      profile.businessCity,
      profile.businessState,
      profile.businessZip,
    ]
      .map((p) => (p || "").trim())
      .filter(Boolean);
    return parts.join(", ");
  }, [profile.businessStreet, profile.businessCity, profile.businessState, profile.businessZip]);

  const businessAddressLink = useMemo(
    () =>
      businessAddressText && profile.visibility?.businessAddress !== false
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddressText)}`
        : "",
    [businessAddressText, profile.visibility?.businessAddress]
  );

  const publicProfileLink = useMemo(() => {
    if (profile.publicLink) return profile.publicLink;
    if (publicSlug && typeof window !== "undefined") return `${window.location.origin}/p/${publicSlug}`;
    return "";
  }, [profile.publicLink, publicSlug]);

  const clearbadgeProfileLink = useMemo(() => {
    if (!publicSlug) return "";
    if (typeof window === "undefined") return "";
    return `${window.location.origin}/p/${publicSlug}`;
  }, [publicSlug]);

  const badgeBase = useMemo(() => {
    if (API_BASE) return API_BASE.replace(/\/$/, "");
    if (typeof window !== "undefined") return window.location.origin;
    return "";
  }, []);

  useEffect(() => {
    if (!publicSlug || !badgeBase) return;
    let isActive = true;
    setBadgeStatus({
      state: "loading",
      verified: null,
      checkedAt: null,
      lastVerifiedAt: null,
      profileUrl: "",
      lookupUrl: "",
    });
    const loadBadge = async () => {
      try {
        const res = await fetch(
          `${badgeBase}/api/badge-status/${encodeURIComponent(publicSlug)}?t=${Date.now()}`,
          { cache: "no-store" }
        );
        const data = await res.json().catch(() => null);
        if (!res.ok || !data) throw new Error("Badge status unavailable");
        if (!isActive) return;
        setBadgeStatus({
          state: "loaded",
          verified: !!data.verified,
          checkedAt: data.checked_at || null,
          lastVerifiedAt: data.last_verified_at || null,
          profileUrl: data.profile_url || "",
          lookupUrl: data.lookup_url || "",
        });
      } catch (_err) {
        if (!isActive) return;
        setBadgeStatus({
          state: "error",
          verified: null,
          checkedAt: null,
          lastVerifiedAt: null,
          profileUrl: "",
          lookupUrl: "",
        });
      }
    };
    loadBadge();
    return () => {
      isActive = false;
    };
  }, [badgeBase, publicSlug]);

  useEffect(() => {
    const generateQr = async () => {
      if (!publicProfileLink) {
        setQrDataUrl("");
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(publicProfileLink);
        setQrDataUrl(dataUrl);
      } catch {
        setQrDataUrl("");
      }
    };
    generateQr();
  }, [publicProfileLink]);

  const downloadVCard = () => {
    const fn = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim() || "Contact";
    const org = profile.company || "";
    const title = profile.visibility?.title === false ? "" : profile.title || "";
    const email = profile.visibility?.email !== false ? profile.email || "" : "";
    const phone = profile.visibility?.phone !== false ? profile.phone || "" : "";
    const businessAddress =
      businessAddressText && profile.visibility?.businessAddress !== false ? businessAddressText : "";
    const socialVCard = socialLinks.map((link) => `URL;TYPE=${link.label}:${link.url}`);
    const websiteVCard = websiteLinks.map((link) => `URL;TYPE=Website:${link.url}`);
    const adrParts = [
      "", // PO Box
      "", // Extended
      profile.businessStreet || "",
      profile.businessCity || "",
      profile.businessState || "",
      profile.businessZip || "",
      "", // Country/region (omit county from card/vCard)
    ];
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${fn}`,
      `N:${profile.lastName || ""};${profile.firstName || ""};;;`,
      org ? `ORG:${org}` : "",
      title ? `TITLE:${title}` : "",
      email ? `EMAIL;TYPE=INTERNET:${email}` : "",
      phone ? `TEL;TYPE=CELL:${phone}` : "",
      businessAddress ? `ADR;TYPE=WORK:${adrParts.join(";")}` : "",
      ...websiteVCard,
      ...socialVCard,
      "END:VCARD",
    ]
      .filter(Boolean)
      .join("\n");

    const blob = new Blob([vcard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    const isMobile = getDeviceType() === "mobile";

    if (isMobile) {
      const mobileUrl = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}`;
      window.location.href = mobileUrl;
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fn.replace(/\s+/g, "_") || "contact"}.vcf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const logEvent = async (type) => {
    if (!publicSlug) return;
    try {
      await fetch(`${API_BASE}/api/lead-event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          publicSlug,
          type,
          deviceType: getDeviceType(),
        }),
      });
    } catch (e) {
      // no-op
    }
  };

  const submitLead = async () => {
    if (!publicSlug) return;
    setSubmittingLead(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token || "";
      const res = await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          publicSlug,
          leadType: "share_contact",
          name: leadName.trim(),
          email: leadEmail.trim(),
          phone: leadPhone.trim(),
          deviceType: getDeviceType(),
        }),
      });
      if (res.ok) {
        const leadProfileId = profileId || publicSlug || "";
        trackGoal("lead_generated", { profile_id: leadProfileId, source: "profile" });
      }
    } catch (e) {
      // allow continue even if logging fails
    } finally {
      setSubmittingLead(false);
    }
  };

  useEffect(() => {
    return () => {
      if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
    };
  }, []);

  const handleSaveContact = async () => {
    // Download first, then show modal after delay
    await logEvent("vcard_downloaded");
    trackGoal("digital_card_shared", { method: "link" });
    downloadVCard();
    if (modalTimerRef.current) clearTimeout(modalTimerRef.current);
    modalTimerRef.current = setTimeout(() => {
      setLeadModalOpen(true);
    }, 3000);
  };

  const handleSkip = async () => {
    setLeadModalOpen(false);
  };

  const handleContinue = async () => {
    await submitLead();
    setLeadModalOpen(false);
    setThankYouOpen(true);
  };

  useFocusTrap({
    isOpen: leadModalOpen,
    containerRef: leadModalRef,
    onClose: () => {
      if (!submittingLead) setLeadModalOpen(false);
    },
    initialFocusRef: leadSkipButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: thankYouOpen,
    containerRef: thankYouModalRef,
    onClose: () => setThankYouOpen(false),
    initialFocusRef: thankYouCloseButtonRef,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: licenseModalOpen,
    containerRef: licenseModalRef,
    onClose: () => setLicenseModalOpen(false),
    initialFocusRef: licenseModalCloseRef,
    lockScroll: true,
  });

  const formatPhoneInput = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 10);
    const parts = [];
    if (digits.length > 0) parts.push(digits.slice(0, 3));
    if (digits.length >= 4) parts.push(digits.slice(3, 6));
    if (digits.length >= 7) parts.push(digits.slice(6, 10));
    return parts.length === 0 ? "" : parts.length === 1 ? parts[0] : `(${parts[0]}) ${parts[1]}${parts[2] ? `-${parts[2]}` : ""}`;
  };

  const formatPhoneDisplay = (val) => {
    if (!val) return "";
    let digits = val.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("1")) {
      digits = digits.slice(1);
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 7) {
      return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    }
    return val;
  };

  const formatBadgeDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const displayPhone = formatPhoneDisplay(profile.phone);
  const badgeLabel =
    badgeStatus.state === "loaded"
      ? badgeStatus.verified
        ? "Verified"
        : "Not Verified"
      : badgeStatus.state === "error"
        ? "Status unavailable"
        : "Checking...";
  const lastVerifiedLabel = formatBadgeDate(badgeStatus.lastVerifiedAt);
  const checkedLabel = formatBadgeDate(badgeStatus.checkedAt);
  const badgeTitle = lastVerifiedLabel
    ? `Last verified ${lastVerifiedLabel}`
    : checkedLabel
      ? `Checked ${checkedLabel}`
      : "Live verification status";
  const badgeLink = badgeStatus.lookupUrl || clearbadgeProfileLink || badgeStatus.profileUrl || publicProfileLink;
  const badgeClassName = [
    "vettify-pill",
    badgeStatus.state === "loading" || badgeStatus.state === "error" ? "loading" : "",
    badgeStatus.state === "loaded" && badgeStatus.verified === false ? "unverified" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const renderLicenseButton = (lic) => (
    <button
      type="button"
      className="card-license-pill clickable"
      style={{
        background: accentLight,
        borderColor: accentLightBorder,
      }}
      onClick={(e) => {
        e.stopPropagation();
        handleOpenLicense(lic);
      }}
    >
      <LicenseIcon className="card-line-icon accent license-icon" />
      <span className="card-license-title">{licenseLabel(lic)}</span>
    </button>
  );
  const licenseListModal =
    licenseModalOpen && typeof document !== "undefined"
      ? createPortal(
        <div className="license-list-modal-backdrop" onClick={() => setLicenseModalOpen(false)}>
          <div
            ref={licenseModalRef}
            className="license-list-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="digital-card-license-list-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="license-list-modal-header">
              <h4 id="digital-card-license-list-title">Licenses</h4>
              <button
                ref={licenseModalCloseRef}
                type="button"
                className="license-list-modal-close"
                onClick={() => setLicenseModalOpen(false)}
                aria-label="Close licenses"
              >
                <svg
                  className="modal-close-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
            <div className="license-list-modal-body">
              {sortedLicenses.map((lic) => (
                <div key={lic.id} className="license-list-modal-row">
                  {renderLicenseButton(lic)}
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )
      : null;

  return (
    <div
      className={`digital-card digital-card--scoped flip-wrapper ${isFlipped ? "is-flipped" : ""}`}
      style={{
        "--card-accent": accent,
        "--card-accent-dark": accentDark,
        "--card-accent-light": accentLight,
        "--card-accent-pill": accentLight,
        "--card-accent-pill-border": accentLightBorder,
        "--card-texture-tint": `rgba(${overlayRgb},0.32)`,
      }}
    >
      <div className="flip-inner">
        <div className="flip-face front-face">
          <div
            className="digital-card__photo"
            data-custom-bg={cardBackgroundUrl ? "true" : "false"}
            style={
              cardBackgroundUrl
                ? {
                    backgroundImage: `url(${cardBackgroundUrl})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }
                : { background: accent }
            }
          >
            <div className="digital-card__avatar">
              {profile.profilePhoto ? (
                <img src={profile.profilePhoto} alt="Profile" />
              ) : (
                <div className="avatar xlarge">{initials}</div>
              )}
            </div>
            {qrDataUrl && (
              <button
                type="button"
                className="qr-photo-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  trackGoal("digital_card_shared", { method: "qr" });
                  setIsFlipped(true);
                }}
                aria-label="Show QR code"
              >
                <span className="qr-dot">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="3" y="3" width="6" height="6" rx="1.5"></rect>
                    <rect x="15" y="3" width="6" height="6" rx="1.5"></rect>
                    <rect x="3" y="15" width="6" height="6" rx="1.5"></rect>
                    <path d="M15 15h6v6h-6z"></path>
                    <path d="M12 7h1"></path>
                    <path d="M7 12v1"></path>
                    <path d="M12 16h1"></path>
                    <path d="M16 12v1"></path>
                  </svg>
                </span>
              </button>
            )}
          </div>

          <div className="digital-card__body">
            <h3 className="card-name">{[profile.firstName, profile.lastName].filter(Boolean).join(" ") || "First Last"}</h3>
            {profile.title && profile.visibility?.title !== false && <p className="card-line tight card-title">{profile.title}</p>}
            {websiteLinks.length > 0 && (
              <div className="card-links">
                {websiteLinks.map((link, idx) => (
                  <p className="card-line tight" key={`${link.url}-${idx}`}>
                    <GlobeIcon className="card-line-icon accent" />
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="card-line-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {link.label || formatWebsiteLabel(link.url)}
                    </a>
                  </p>
                ))}
              </div>
            )}
            {profile.visibility?.company !== false && (
              <p className="card-line tight">
                <CompanyIcon className="card-line-icon accent" />
                {profile.company || "company name"}
              </p>
            )}
            {businessAddressText && profile.visibility?.businessAddress !== false && (
              <p className="card-line tight">
                <BusinessAddressIcon className="card-line-icon accent" />
                {businessAddressLink ? (
                  <a href={businessAddressLink} target="_blank" rel="noopener noreferrer" className="card-line-link">
                    {businessAddressText}
                  </a>
                ) : (
                  businessAddressText
                )}
              </p>
            )}
            {profile.visibility?.email && (
              <p className="card-line tight">
                <EmailIcon className="card-line-icon accent" />
                {profile.email || "email"}
              </p>
            )}
            {profile.visibility?.phone && (
              <p className="card-line tight">
                <PhoneIcon className="card-line-icon accent" />
                {displayPhone || profile.phone || "phone"}
              </p>
            )}
            {socialLinks.length > 0 && (
              <div className="card-socials">
                {socialLinks.map(({ key, url, label, Icon }) => (
                  <a
                    key={key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card-social-link"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={label}
                  >
                    <Icon className="card-social-icon" />
                  </a>
                ))}
              </div>
            )}
            {publicSlug && badgeLink && (
              <a
                href={badgeLink}
                target="_blank"
                rel="noopener noreferrer"
                className={badgeClassName}
                title={badgeTitle}
                aria-label={badgeLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  logEvent("vettify_profile_click");
                }}
              >
                <img src={clearBadgeMark} alt="ClearBadge" className="vettify-pill__icon" />
                <span className="vettify-pill__text">{badgeLabel}</span>
              </a>
            )}

            {hasLicense && <div className="card-divider dotted" />}

            {hasLicense && (
              <div className="card-licenses scrollable-licenses">
                {sortedLicenses.map((lic) => (
                  <div key={lic.id} style={{ width: "100%" }}>
                    {renderLicenseButton(lic)}
                  </div>
                ))}
              </div>
            )}
            {hasLicense && (
              <div className="mobile-license-actions">
                <button
                  type="button"
                  className="primary mobile-license-open-btn"
                  style={{ background: accent, border: "none" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLicenseModalOpen(true);
                  }}
                >
                  <LicenseIcon className="card-line-icon" />
                  See Licenses
                </button>
              </div>
            )}
          </div>

          <button
            className="primary card-cta solid card-cta-outer"
            style={{ background: accent, border: "none" }}
            onClick={handleSaveContact}
          >
            <SaveIcon className="card-line-icon" /> Save Contact
          </button>
        </div>

        <div className="flip-face back-face">
          <div className="card-back">
            <div className="card-back-header">
              <span className="muted">Scan to save</span>
              <button
                type="button"
                className="icon-btn back-close"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFlipped(false);
                }}
                aria-label="Back to card"
              >
                <span className="qr-dot">
                  <UserIcon className="card-line-icon" />
                </span>
              </button>
            </div>
            <div className="qr-panel">
              {qrDataUrl ? <img src={qrDataUrl} alt="QR code" /> : <div className="qr-placeholder">QR</div>}
              <p className="muted" style={{ marginTop: 12, textAlign: "center" }}>
                Point your camera at the QR code to view this card.
              </p>
            </div>
          </div>
        </div>
      </div>

      {licenseListModal}

      {leadModalOpen && (
        <div className="lead-modal-backdrop" onClick={() => !submittingLead && setLeadModalOpen(false)}>
          <div
            ref={leadModalRef}
            className="lead-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="digital-card-lead-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lead-modal-header">
              <div className="lead-title-block">
                <p className="lead-eyebrow">Share your information with</p>
                <h4 id="digital-card-lead-title" className="lead-name">
                  {[profile.firstName, profile.lastName].filter(Boolean).join(" ") || "This contact"}
                </h4>
              </div>
              <button ref={leadSkipButtonRef} className="ghost small" type="button" onClick={handleSkip} disabled={submittingLead}>
                Skip
              </button>
            </div>
            <div className="lead-form">
              <label>
                <span>Your name</span>
                <input
                  type="text"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  placeholder="Your name"
                />
              </label>
              <label>
                <span>Your email</span>
                <input
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </label>
              <label>
                <span>Your phone number</span>
                <input
                  type="tel"
                  value={leadPhone}
                  onChange={(e) => setLeadPhone(formatPhoneInput(e.target.value))}
                  placeholder="(555) 123-4567"
                />
              </label>
              <p className="lead-privacy">
                <span aria-hidden="true" className="lead-lock">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="lead-lock-icon">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                  </svg>
                </span>
                Your data will always be kept private.
              </p>
              <button
                className="primary wide"
                type="button"
                onClick={handleContinue}
                disabled={submittingLead}
              >
                {submittingLead ? "Saving..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {thankYouOpen && (
        <div className="lead-modal-backdrop" onClick={() => setThankYouOpen(false)}>
          <div
            ref={thankYouModalRef}
            className="lead-modal lead-modal-thanks"
            role="dialog"
            aria-modal="true"
            aria-labelledby="digital-card-thank-you-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 id="digital-card-thank-you-title">Thanks for sharing!</h4>
            <p className="muted" style={{ textAlign: "center", marginTop: 8 }}>
              Your contact info was sent. We'll be in touch soon.
            </p>
            <button
              ref={thankYouCloseButtonRef}
              className="primary wide"
              type="button"
              onClick={() => setThankYouOpen(false)}
              style={{ marginTop: 16 }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
